import { NotificationTypeDto, type NotificationDto } from "../../autogen/bff-api";
import { urls } from "../../urls";
import type { PortalEventUrls } from "../sourcing-events/portal/usePortalEventUrls";
import {
  requireContractDocumentMeta,
  requireContractReminderMeta,
  requireContractkMeta,
  requireSeDoffinPublicationFailedMeta,
  requireSeDoffinPublishedMeta,
  requireSeResponsibleBuyerChangedMeta,
  requireNeedReportedMeta,
  requireNeedStatusChangedMeta,
  requireSeJuryConfirmationRequestedMeta,
  requireSourcingEventAnnouncementMeta,
  requireFrameworkPanelJoinedMeta,
  requireFrameworkPanelStatusChangedMeta,
  requireFrameworkValueReminderMeta,
  requireSeV2MiniCompetitionInviteMeta,
  requireSourcingEventMeta,
  requireSourcingEventQuestionMeta,
  requireSeInvitedToTenderMeta,
  requireSeInvitedToConfirmInterestMeta,
  requireSeOrganizationInviteMeta,
  requireSeDpsAdmittedMeta,
  requireSeQualificationSchemeAdmittedMeta,
  requireSeQualificationSchemeRemovedMeta,
  requireSeQualificationSchemeRemovalWithdrawnMeta,
  requireSeDpsRemovedMeta,
  requireSeBidRejectedMeta,
  requireSeRequestNotQualifiedMeta,
  requireSeRequestNotSelectedMeta,
  requireTaskMeta,
} from "./get-notification-description";

export const getNotificationLink = (
  notification: NotificationDto,
  selectedOrgId: string,
  portalView: PortalEventUrls["view"],
): string | undefined => {
  switch (notification.type) {
    case NotificationTypeDto.ContractDateReminder: {
      const meta = requireContractReminderMeta(notification);
      return urls.app.contracts.view.go(meta.contractId);
    }
    case NotificationTypeDto.ContractDocumentCompletedSigning: {
      const meta = requireContractDocumentMeta(notification);
      return urls.app.contracts.view.documents(meta.contractId);
    }
    case NotificationTypeDto.ContractSignatureDeadlineExpired: {
      const meta = requireContractDocumentMeta(notification);
      return urls.app.contracts.view.documents(meta.contractId);
    }
    case NotificationTypeDto.ContractSharedWithDepartment:
    case NotificationTypeDto.ContractSharedWithOrg:
    case NotificationTypeDto.CallOffCompletedByOutsideOrg:
    case NotificationTypeDto.ContractSharedWithPerson: {
      const meta = requireContractkMeta(notification);
      return urls.app.contracts.view.go(meta.contractId);
    }
    case NotificationTypeDto.ContractMessageReceived: {
      const meta = requireContractkMeta(notification);
      return urls.app.contracts.view.dialog(meta.contractId);
    }
    case NotificationTypeDto.ContractTaskCommentAdded:
    case NotificationTypeDto.ContractTaskDeleted:
    case NotificationTypeDto.ContractTaskDueDateUpdated:
    case NotificationTypeDto.ContractTaskDelegated:
    case NotificationTypeDto.ContractTaskStatusUpdated:
    case NotificationTypeDto.ContractTaskDueDateReminder: {
      const meta = requireTaskMeta(notification);
      return urls.app.contracts.view.task(meta.contractId, meta.taskId);
    }
    case NotificationTypeDto.SeV2NewQuestion:
    case NotificationTypeDto.SeV2QuestionAnswered:
    case NotificationTypeDto.SeV2ClarificationMessageToBuyer:
    case NotificationTypeDto.SeV2ClarificationMessageToSupplier:
    case NotificationTypeDto.SeV2ClarificationReplyDeadlineReminder: {
      const meta = requireSourcingEventQuestionMeta(notification);
      return meta.receiverRole === "Buyer"
        ? urls.app.sourcingEvents.view.question(meta.eventId, meta.questionId)
        : portalView.question(meta.eventId, meta.questionId);
    }
    case NotificationTypeDto.SeV2InformationPublished: {
      const meta = requireSourcingEventAnnouncementMeta(notification);
      return portalView.announcement(meta.eventId, meta.announcementId);
    }
    case NotificationTypeDto.SeV2BidDelivered: {
      const meta = requireSourcingEventMeta(notification);
      return `${urls.app.sourcingEvents.view.go(meta.eventId)}?tab=bids`;
    }
    case NotificationTypeDto.SeV2ResultNoticeDeadlineReminder:
      return urls.app.sourcingEvents.view.go(requireSourcingEventMeta(notification).eventId);
    case NotificationTypeDto.SeV2BidDeadlineReminder: {
      const meta = requireSourcingEventMeta(notification);
      return meta.eventOwningOrganizationId === selectedOrgId
        ? urls.app.sourcingEvents.view.go(meta.eventId)
        : portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2AwardLetterDispatched:
    case NotificationTypeDto.SeV2AwardDecided:
    case NotificationTypeDto.SeV2AwardDecisionRevoked:
    case NotificationTypeDto.SeV2BidRejectionRevoked:
    case NotificationTypeDto.SeV2EventCancelled:
    case NotificationTypeDto.SeV2EventClosed:
    case NotificationTypeDto.SeV2RoundPublished:
    case NotificationTypeDto.SeV2EventChanged: {
      const meta = requireSourcingEventMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2MiniCompetitionInvite: {
      const meta = requireSeV2MiniCompetitionInviteMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.FrameworkPanelJoined: {
      requireFrameworkPanelJoinedMeta(notification);
      return urls.app.supplier.frameworkAgreements;
    }
    case NotificationTypeDto.FrameworkValueReminder: {
      const meta = requireFrameworkValueReminderMeta(notification);
      return urls.app.frameworkAgreements.view.go(meta.frameworkAgreementId);
    }
    case NotificationTypeDto.FrameworkPanelStatusChanged: {
      requireFrameworkPanelStatusChangedMeta(notification);
      return urls.app.supplier.frameworkAgreements;
    }
    case NotificationTypeDto.SeV2InvitedToTender: {
      const meta = requireSeInvitedToTenderMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2InvitedToConfirmInterest: {
      const meta = requireSeInvitedToConfirmInterestMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2OrganizationInvite: {
      const meta = requireSeOrganizationInviteMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2BidRejected: {
      const meta = requireSeBidRejectedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2RequestNotQualified: {
      const meta = requireSeRequestNotQualifiedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2RequestNotSelected: {
      const meta = requireSeRequestNotSelectedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DpsAdmitted: {
      const meta = requireSeDpsAdmittedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2QualificationSchemeAdmitted: {
      const meta = requireSeQualificationSchemeAdmittedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2QualificationSchemeRemoved: {
      const meta = requireSeQualificationSchemeRemovedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2QualificationSchemeRemovalWithdrawn: {
      const meta = requireSeQualificationSchemeRemovalWithdrawnMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DpsRemoved: {
      const meta = requireSeDpsRemovedMeta(notification);
      return portalView.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2ResponsibleBuyerChanged: {
      const meta = requireSeResponsibleBuyerChangedMeta(notification);
      return urls.app.sourcingEvents.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2JuryConfirmationRequested: {
      const meta = requireSeJuryConfirmationRequestedMeta(notification);
      return urls.app.sourcingEvents.view.evaluation(meta.eventId);
    }
    case NotificationTypeDto.SeV2DoffinPublished: {
      const meta = requireSeDoffinPublishedMeta(notification);
      return urls.app.sourcingEvents.view.go(meta.eventId);
    }
    case NotificationTypeDto.SeV2DoffinPublicationFailed: {
      const meta = requireSeDoffinPublicationFailedMeta(notification);
      return urls.app.sourcingEvents.view.go(meta.eventId);
    }
    case NotificationTypeDto.OrganizationMerge:
      return `${urls.app.users}?tab=duplicates`;
    case NotificationTypeDto.NeedReported:
      return urls.app.needs.view.go(requireNeedReportedMeta(notification).needId);
    case NotificationTypeDto.NeedStatusChanged:
      return urls.app.needs.view.go(requireNeedStatusChangedMeta(notification).needId);
  }
};
